import { useEffect, useState } from 'react';
import { useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';

const isTextInput = ({ target }) =>
  target.tagName === 'INPUT' && target.getAttribute('type') === 'text';

const elementHasParentWithDNDParam = ({ target }) => {
  const maxLoopChain = 10;

  const hasDisabledDNDProp = (el, prop, v, max) => {
    if (!el || max <= 0) {
      return false;
    }
    if (el.getAttribute(prop) === v) {
      return true;
    }

    return hasDisabledDNDProp(el.parentElement, prop, v, max - 1);
  };

  return hasDisabledDNDProp(target, 'data-no-dnd', 'true', maxLoopChain);
};

class PointerSensorAvoidingPreventDefault extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown',
      handler: ({ nativeEvent }) => {
        const conditionsToPreventDrag = [
          isTextInput,
          elementHasParentWithDNDParam,
        ].some(testCondition => testCondition(nativeEvent));

        return !(
          nativeEvent.target.getAttribute('data-no-dnd') === 'true' ||
          conditionsToPreventDrag
        );
        // comment out preventDefault is necessary to work
        // nativeEvent.preventDefault();
      },
    },
  ];
}

export const useDragNDropList = ({ items, onChange }) => {
  const [itemsToRender, setItemsToRender] = useState(() =>
    items.map((item, index) => ({
      id: item?.id ?? index + 1,
      component: item.component ?? item,
      data: item?.data,
    }))
  );

  useEffect(() => {
    setItemsToRender(
      items.map((item, index) => ({
        id: item?.id ?? index + 1,
        component: item.component ?? item,
        data: item?.data,
      }))
    );
  }, [items]);

  const [activeItem, setActiveItem] = useState();

  const sensors = useSensors(useSensor(PointerSensorAvoidingPreventDefault));

  const onDragStart = ({ active }) => {
    const itemToRenderOverlay = itemsToRender.find(i => i.id === active.id);
    setActiveItem(itemToRenderOverlay);
  };

  const onDragEnd = event => {
    const { active, over } = event;

    if (active?.id !== over?.id) {
      setItemsToRender(itemsKey => {
        const oldIndex = itemsKey.findIndex(item => item.id === active.id);
        const newIndex = itemsKey.findIndex(item => item.id === over?.id);
        const arrayResult = arrayMove(itemsKey, oldIndex, newIndex);
        onChange && onChange(arrayResult);
        return arrayResult;
      });
    }
    setActiveItem();
  };

  const onDragCancel = () => setActiveItem();

  return {
    sensors,
    itemsToRender,
    setItemsToRender,
    activeItem,
    setActiveItem,
    onDragStart,
    onDragEnd,
    onDragCancel,
  };
};
